<template>
  <v-navigation-drawer
    v-model="navigationDrawerVisible"
    app
    class="elevation-0"
    color="brand-card-bg"
    right
    style="max-width: 600px; min-height: 100vh !important; height: -webkit-fill-available !important; min-height: -webkit-fill-available !important;"
    temporary
    width="95%"
  >
    <template #prepend>
      <v-toolbar
        class="mx-2"
        color="brand-card-bg"
        flat
      >
        <v-badge
          :value="cartProductsCount > 0"
          bordered
          class="order-badge"
          color="brand-beige-light"
          dark
          overlap
        >
          <template #badge>
            <span class="brand-green-dark--text">{{ cartProductsCount }}</span>
          </template>

          <v-icon
            color="brand-green-dark"
            size="26"
          >
            mdi-cart
          </v-icon>
        </v-badge>

        <v-toolbar-title class="ml-6 brand-text-heading">
          {{ $t('Cart.Title').toLocaleUpperCase($i18n.locale) }}
        </v-toolbar-title>

        <v-spacer />

        <v-btn
          class="brand-beige ma-0"
          color="brand-green-dark"
          icon
          small
          @click="toggleNavigationDrawer"
        >
          <v-icon>
            mdi-chevron-right
          </v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider />
    </template>

    <template v-if="cartProductsCount">
      <template v-if="canShowOrderType">
        <order-type-selector
          class="px-4"
          show-selected-name-only
        />
        <v-divider />
      </template>

      <template v-if="canShowQrData">
        <qr-code-order-data class="px-6 py-2" />
        <v-divider />
      </template>

      <template v-if="cartSmsService">
        <div
          class="px-4 py-2"
        >
          <v-alert
            border="left"
            class="mb-0 pr-2 text-no-select"
            color="brand-primary"
            colored-border
            dense
            text
            type="info"
          >
            {{ getTranslatedField(smsServiceCharge) }}

            <template #prepend>
              <v-icon
                class="mr-2"
                color="brand-primary"
                size="28"
              >
                mdi-cellphone-message
              </v-icon>
            </template>

            <template #append>
              {{ cartSmsService.amount | currency }}
            </template>
          </v-alert>
        </div>
        <v-divider />
      </template>

      <template v-if="canShowScheduled">
        <scheduled-order class="px-6 py-0" />
        <v-divider />
      </template>

      <template v-if="canShowCoupons">
        <v-container
          fluid
          px-6
          py-0
        >
          <v-row dense>
            <v-col
              v-if="!cartCoupon"
              cols="12"
            >
              <v-expansion-panels
                accordion
                flat
              >
                <v-expansion-panel class="transparent">
                  <v-expansion-panel-header class="px-0 py-1 brand-green-dark--text">
                    <div class="d-flex">
                      <v-icon
                        class="mr-6"
                        color="brand-green-dark"
                      >
                        mdi-tag
                      </v-icon>

                      <span class="brand-text-heading">{{ $t('Cart.Coupon.Title').toLocaleUpperCase($i18n.locale) }}</span>
                    </div>
                  </v-expansion-panel-header>

                  <v-expansion-panel-content class="cart-coupon">
                    <v-sheet
                      class="pl-12 rounded"
                      color="transparent"
                    >
                      <v-text-field
                        v-model="couponValue"
                        :dense="$vuetify.breakpoint.xsOnly"
                        :label="$t('Cart.Coupon.Label')"
                        clearable
                        color="brand-green"
                        hide-details="auto"
                        outlined
                      />

                      <v-btn
                        :disabled="couponLoading"
                        :large="$vuetify.breakpoint.xsOnly"
                        :loading="couponLoading"
                        :x-large="$vuetify.breakpoint.smAndUp"
                        block
                        class="mt-2"
                        color="brand-green brand-text-title brand-green-dark--text"
                        depressed
                        @click="cartAddCoupon(couponValue)"
                      >
                        {{ $t('Cart.Coupon.Button.Apply').toLocaleUpperCase($i18n.locale) }}
                      </v-btn>
                    </v-sheet>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>

            <v-col
              v-else
              cols="12"
            >
              <v-list class="pa-0">
                <v-list-item
                  class="px-0"
                  style="height: 72px;"
                >
                  <div class="left mr-6">
                    <v-icon color="brand-green-dark">
                      mdi-tag
                    </v-icon>
                  </div>

                  <v-list-item-content>
                    <v-list-item-title class="brand-text-heading brand-green-dark--text">
                      {{ $t('Cart.Coupon.Item') }}
                    </v-list-item-title>

                    <v-list-item-subtitle class="brand-grey--text">
                      {{ cartCoupon.code }}
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-list-item-action-text class="body-2 brand-green-dark--text">
                      -{{ cartCoupon.amount | currency }}
                    </v-list-item-action-text>

                    <v-btn
                      :disabled="couponLoading"
                      :loading="couponLoading"
                      class="pa-0"
                      color="brand-green-dark"
                      plain
                      style="min-width: 36px;"
                      @click.stop="cartRemoveCoupon(cartCoupon.code)"
                    >
                      <v-icon color="brand-green-dark">
                        mdi-close
                      </v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-container>

        <v-divider />
      </template>

      <v-list three-line>
        <template v-if="AppConfig.Cart.GroupByStore">
          <template v-for="(store, storeIdx) in cartProductsGroupedByStore">
            <div
              :key="`cart-store-${storeIdx}`"
              class="px-6 py-2 d-flex align-center fill-width"
              style="cursor: pointer"
              @click="gotoStoreCatalog(store.Id)"
            >
              <v-avatar
                v-if="AppConfig.Cart.GroupShowStoreLogo"
                class="mr-2"
                color="brand-green-dark"
                size="40"
                style="border: var(--v-brand-green-dark-base) solid 1px;"
              >
                <img :src="store.Logo">
              </v-avatar>

              <div class="brand-text-title brand-green-dark--text">
                {{ store.Name.toLocaleUpperCase($i18n.locale) }}
              </div>

              <v-spacer />

              <div class="pl-2">
                <v-chip
                  v-if="storeIsOpened(store)"
                  class="brand-green-dark--text px-1"
                  color="brand-beige"
                  label
                  small
                >
                  <v-icon
                    class="mr-1"
                    small
                  >
                    mdi-clock
                  </v-icon>

                  <span class="text-body">{{ storeTime(store) }}</span>
                </v-chip>

                <v-chip
                  v-else
                  color="brand-beige"
                  dark
                  label
                  small
                >
                  <span class="text-body">{{ $t('Store.Status.Closed').toLocaleUpperCase($i18n.locale) }}</span>
                </v-chip>
              </div>
            </div>

            <div
              v-if="cartStoresCount > 1 && !orderTotalAmountValidStore(store)"
              :key="`cart-store-alert-${storeIdx}`"
              class="px-4 pb-1 d-flex align-center fill-width"
            >
              <cart-store-minimum-order-warning
                :store-id="store.Id || store.StoreId"
                dense
                sort-text
                text
              />
            </div>

            <template v-for="(product, productIdx) in store.Items">
              <v-lazy
                :key="`cart-store-${storeIdx}-product-lazy-${productIdx}`"
                min-height="88"
                transition="fade-transition"
              >
                <cart-product-list-item
                  :key="`cart-store-${storeIdx}-product-${productIdx}`"
                  :product="product"
                  :store-logo="AppConfig.Cart.ProductShowStoreLogo"
                  class="mx-3"
                />
              </v-lazy>

              <v-divider
                :key="`cart-store-${storeIdx}-product-divider-${productIdx}`"
                class="mx-6"
              />
            </template>
          </template>
        </template>

        <template v-else>
          <template v-for="(product, idx) in AppCart.Items">
            <v-lazy
              :key="`cart-product-lazy-${idx}`"
              min-height="88"
              transition="fade-transition"
            >
              <cart-product-list-item
                :key="`cart-product-${idx}`"
                :product="product"
                :product-index="idx"
              />
            </v-lazy>

            <v-divider
              :key="`cart-product-divider-${idx}`"
              class="mx-4"
            />
          </template>
        </template>
      </v-list>

      <v-list
        v-if="cartExtraChargesUnique.length"
        class="px-6"
      >
        <div class="d-flex align-center fill-width">
          <div class="brand-text-title brand-green-dark--text">
            {{ $t('Cart.ExtraCharges.Title').toLocaleUpperCase($i18n.locale) }}
          </div>
        </div>

        <template v-for="(charge, chargeIdx) in cartExtraChargesUnique">
          <v-list-item
            :key="`cart-charge-lazy-${chargeIdx}`"
            class="px-2"
            dense
            style="min-height: 20px; height: 30px;"
          >
            <v-list-item-title class="brand-text-small">
              <template v-if="charge.lang && charge.lang[$i18n.locale]">
                <strong>{{ charge.quantity }}x</strong> {{ charge.lang[$i18n.locale].Name }}
              </template>
              <template v-else>
                {{ charge.title }}
              </template>
            </v-list-item-title>

            <v-list-item-action>
              <v-list-item-action-text class="brand-text-small brand-green-dark--text text-right font-weight-bold">
                {{ charge.amount | currency }}
              </v-list-item-action-text>
            </v-list-item-action>
          </v-list-item>

          <v-divider :key="`cart-charge-lazy-divider-${chargeIdx}`" />
        </template>
      </v-list>

      <v-container
        v-if="recommendedStores.length"
        fluid
      >
        <brand-slider
          :stores="recommendedStores"
          :title="$t('Cart.BrandSlider.Title')"
          class="mx-1"
          dense
          title-class="brand-text-heading brand-green-dark--text"
        />
      </v-container>
    </template>

    <template v-else>
      <v-container
        :class="{'align-start': canShowOrderType || canShowQrData}"
        class="text-no-select"
        fill-height
        fluid
      >
        <v-row v-if="canShowOrderType">
          <v-col
            class="py-2"
            cols="12"
          >
            <order-type-selector
              class="px-0 pt-0"
              show-selected-name-only
            />
            <v-divider />
          </v-col>
        </v-row>

        <v-row v-if="canShowQrData">
          <v-col
            class="py-2"
            cols="12"
          >
            <qr-code-order-data class="px-1 pt-0 pb-2" />
            <v-divider />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <div class="text-center">
              <div class="mb-6">
                <v-icon
                  color="brand-primary"
                  size="192"
                >
                  mdi-basket-plus
                </v-icon>
              </div>

              <h2 class="brand-text-title brand-green-dark--text">
                {{ toLocaleUpperCase($t('Cart.Message.Info.NoItems.Title')) }}
              </h2>

              <h5 class="mt-4 brand-green-dark--text">
                {{ $t('Cart.Message.Info.NoItems.Subtitle') }}
              </h5>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </template>

    <template #append>
      <v-divider />

      <v-container fluid>
        <v-row>
          <v-col
            v-if="cartProductsCount"
            class="px-6"
            cols="12"
          >
            <v-btn
              v-if="isMinimumOrderTotalValid"
              :disabled="IsLoading || !isMinimumOrderTotalValid"
              block
              class="brand-green-dark--text pl-2 pr-3"
              color="brand-primary"
              depressed
              x-large
              @click="onCheckoutDetails"
            >
              <v-badge
                v-if="cartProductsCount"
                bordered
                class="mr-2 order-badge"
                color="brand-beige-light"
                dark
                inline
              >
                <template #badge>
                  <span class="brand-green-dark--text">{{ cartProductsCount }}</span>
                </template>
              </v-badge>

              <span class="brand-text-heading">{{ $t('Cart.Button.Checkout') }}</span>

              <v-spacer />

              <span class="ml-4 brand-text-heading">{{ orderTotal | currency }}</span>
            </v-btn>

            <payment-minimum-order-warning
              dense
              sort-text
            />
          </v-col>

          <v-col
            v-else
            class="px-6"
            cols="12"
          >
            <v-btn
              block
              class="text-capitalize pl-2 pr-3"
              color="brand-primary brand-green-dark--text"
              dark
              depressed
              x-large
              @click="onAddProductsClick"
            >
              <span class="brand-text-heading">{{ $t('Cart.Button.AddItems').toLocaleUpperCase($i18n.locale) }}</span>
            </v-btn>
          </v-col>
        </v-row>

        <product-details-dialog
          :edit-mode="productDetailsDialogEditMode"
          :product="selectedProduct"
          :visible.sync="productDetailsDialogVisible"
        />
      </v-container>
    </template>
  </v-navigation-drawer>
</template>

<script>
import CartDrawerMixin from '@/theme/default/views/cart/CartDrawer.js'

export default {
  name  : 'CartDrawer',
  mixins: [CartDrawerMixin]
}
</script>

<style scoped src="@/theme/default/views/cart/CartDrawer.css"></style>
<style scoped>
/deep/ .order-badge .v-badge__badge {
  width: 22px;
  height: 22px;
  padding: 5px;
}
</style>
